import { getData, updateData, deleteData, upsertData } from './utils';
import { createPerStudyDb } from './createPerStudyDb';

// TODO(ts): `value` is arbitrary Recoil atom state, not derivable from a
// GraphQL document or schema.
export type StateRecord = {
  key: string;
  value: any;
};

const NAME_DB_STATE = 'DB_STATE_RECOIL';
const NAME_OBJECT_STORE = 'STATE_RECOIL';
const KEY_OBJECT_STORE = 'key';

const stateDb = createPerStudyDb({
  dbSuffix: NAME_DB_STATE,
  storeName: NAME_OBJECT_STORE,
  keyPath: KEY_OBJECT_STORE,
});

/**
 * Opens the per-profile persisted report state database and caches the handle. Names it from the given key or the profile id fallback and logs the failure when the open rejects.
 *
 * @param key - Study or profile id naming the state database; falls back to the profile id key.
 * @returns A promise resolving once the state database handle has been established or the failure logged.
 */
const openDBState = async (key?: string) => {
  try {
    await stateDb.openDb(key);
  } catch (error) {
    console.error('Failed to open database', error);
  }
};

/**
 * Ensures the state database is open before an operation runs, opening it on demand when boot has not yet finished. Concurrent callers share one open through the per-study handle's coalescing.
 *
 * @returns A promise resolving to the open state database handle (or null when the open failed).
 */
const ensureStateDb = async () => {
  if (!stateDb.db) await openDBState();
  return stateDb.db!;
};

/**
 * Reads one persisted report state entry from the per-profile state database. Opens the database on demand, then delegates to getData against the state object store.
 *
 * @param key - State key the value is stored under.
 * @returns A promise resolving to the stored state record for that key.
 */
const getDataState = async (key: string) =>
  getData<StateRecord>(await ensureStateDb(), NAME_OBJECT_STORE, key);
/**
 * Overwrites one persisted report state entry. Opens the database on demand, then delegates to updateData against the state object store.
 *
 * @param key - State key of the entry to overwrite.
 * @param value - Value written in place of the stored one.
 * @returns A promise resolving to the key produced by the put.
 */
const updateDataState = async (key: string, value: any) =>
  stateDb.runWrite((db) => updateData(db, NAME_OBJECT_STORE, key, value));
/**
 * Removes one persisted report state entry. Opens the database on demand, then delegates to deleteData against the state object store.
 *
 * @param key - State key of the entry to remove.
 * @returns A promise resolving to the deleted key.
 */
const deleteDataState = async (key: string) =>
  stateDb.runWrite((db) => deleteData(db, NAME_OBJECT_STORE, key));
/**
 * Writes one persisted report state entry, inserting it when no record exists yet. Opens the database on demand, then delegates to upsertData against the state object store.
 *
 * @param key - State key to write under.
 * @param value - Value stored for that key.
 * @returns A promise resolving to the key produced by the insert or update.
 */
const upsertDataState = async (key: string, value: any) =>
  stateDb.runWrite((db) => upsertData(db, NAME_OBJECT_STORE, key, value));
/**
 * Empties the persisted report state object store. Opens the database on demand, then delegates to the state database's clear helper.
 *
 * @returns A promise resolving once the state object store has been cleared.
 */
const clearAllDataState = async () => {
  await ensureStateDb();
  return stateDb.clearStore();
};

/**
 * Drops the whole per-study persisted state database. Builds the name from the given key or the profile id fallback, then deletes it.
 *
 * @param key - Study or profile id naming the state database; falls back to the profile id key.
 * @returns A promise resolving once the state database has been deleted.
 */
const deleteDataFromDBState = async (key: string) => {
  await stateDb.removeDb(key);
};

export {
  openDBState,
  getDataState,
  updateDataState,
  deleteDataState,
  upsertDataState,
  clearAllDataState,
  deleteDataFromDBState,
};
